import JwtService from "./jwt";
import StorageService from "./storage.ts";
import ApiService, {type ApiError} from "./api";
import LogService, {LogIslem, LogSayfa} from "./log";
import type {ISoru, IYarisma, IYarismaciCevap, IYarismaKalansure} from "@/model/yarisma";
import {
  type ISoruDTO,
  type IYarismaDTO,
  type IYarismaKalansureDTO,
  mapSoruFromDTO,
  mapYarismaciCevapToDTO,
  mapYarismaFromDTO,
  mapYarismaKalansureFromDTO
} from "@/model/yarisma.dto";
import {TimerService} from "./timer.ts";
import {ref, type Ref} from "vue";
import {router} from "@/router/index.ts";
import {WarningPopup} from "@/utils/popup.ts";
import {i18n} from "@/plugins/i18n";
import YonergeService from "@/services/yonerge.ts";

type GetSoruCevapIndexResult = [null, ISoru[], IYarismaciCevap[], number] | [ApiError];

class YarismaService {
  static async getYarismaBilgisi(): Promise<[ApiError, IYarisma]> {
    const startTime = performance.now();
    const [err, resp] = await ApiService.get<IYarismaDTO>(`yarisma`);
    if (err) {
      LogService.log(LogSayfa.ANASAYFA, LogIslem.HATA, {
        err,
        resp,
        delay: performance.now() - startTime
      });
      return [err, {} as IYarisma];
    }
    return [null, mapYarismaFromDTO(resp!.data)];
  }

  static async getYarismaKalansure(localFirst: boolean = false): Promise<[ApiError, boolean, IYarismaKalansure]> {
    if (localFirst) {
      const lastFetchTime = YarismaService.getKalanSureLastFetchTime();
      // localde veriler varsa ve son 3 dakika içinde sunucudan alınmışsa local veriyi kullan
      const isFresh = lastFetchTime !== null && (Date.now() - lastFetchTime) < 3 * 60 * 1000;
      if (isFresh) {
        const baslamasina = YarismaService.getBaslamasinaKalanSure() ?? 0;
        const bitmesine = YarismaService.getBitmesineKalanSure() ?? 0;
        const yarismaciBitirmisMi = YarismaService.getyarismaciYarismayiBitirmisMi();
        const bittiMi = yarismaciBitirmisMi || bitmesine <= 0;
        return [null, bittiMi, {
          baslangic_tarihi: '',
          baslangic_saati: '',
          baslamasina_kalan_sure: baslamasina,
          bitmesine_kalan_sure: bitmesine,
          yarismaci_bitirmis_mi: yarismaciBitirmisMi,
        }];
      }
    }
    const startTime = performance.now();
    const [err, resp] = await ApiService.get<IYarismaKalansureDTO>(`kalansure`);
    if (err) {
      // status 400 ise sınav bitmiş demektir.
      if (resp?.status === 400) {
        return [err, true, {} as IYarismaKalansure];
      }
      LogService.log(LogSayfa.ANASAYFA, LogIslem.HATA, {
        detay: "getYarismaKalansure",
        err,
        delay: performance.now() - startTime
      });
      return [err, false, {} as IYarismaKalansure];
    }
    const kalansure = mapYarismaKalansureFromDTO(resp!.data);

    // Başarılı sunucu yanıtında fetch zamanını kaydet
    YarismaService.setKalanSureLastFetchTime(Date.now());
    YarismaService.setyarismaciYarismayiBitirmisMi(kalansure.yarismaci_bitirmis_mi);
    return [null, kalansure.yarismaci_bitirmis_mi || kalansure.bitmesine_kalan_sure < 0, kalansure];
  }

  static async getSoruCevapIndex(): Promise<GetSoruCevapIndexResult> {
    let sorular = StorageService.get<ISoru[]>(soruKey());
    let cevaplar = StorageService.get<IYarismaciCevap[]>(cevapKey());
    const index = StorageService.get<number>(indexKey());
    if (sorular && cevaplar && typeof index === "number") {
      return [null, sorular, cevaplar, index];
    }

    const [err, resp] = await ApiService.get<ISoruDTO[]>(`soru`);
    if (err) {
      return [err];
    }

    sorular = mapSoruFromDTO(resp!.data);
    cevaplar = sorular!.map((soru) => ({
      soru_id: soru.id,
      cevap_index: soru.yarismaci_cevap ?? -1,
      client_time: undefined,
    }));

    StorageService.set(soruKey(), sorular);
    StorageService.set(indexKey(), 0);
    YarismaService.setCevaplarStorage(cevaplar);
    return [null, sorular, cevaplar, 0];
  }

  static async cevap(cevap: IYarismaciCevap): Promise<ApiError> {
    const startTime = performance.now();
    const [err] = await ApiService.post(`yarismaci/cevap`, mapYarismaciCevapToDTO(cevap));
    if (err) {
      LogService.log(LogSayfa.YARISMA, LogIslem.HATA, {
        detay: "cevap",
        err,
        delay: performance.now() - startTime
      });
      return err;
    }
    return null;
  }

  static setCevaplarStorage(cevaplar: IYarismaciCevap[]): void {
    StorageService.set(cevapKey(), cevaplar);
  }

  /**
   * Sınavın başlamasına kalan süre kadar rastgele bir süre bekleyip, soruları preload eder.
   * @param baslamasinaKalanSure - Sınavın başlamasına kalan süre
   */
  static sorularPreload(baslamasinaKalanSure: number): TimerService {
    const timeout = Math.floor(Math.random() * (baslamasinaKalanSure - 5)) + 1;
    const sorularTimer = new TimerService(ref(timeout), null, async () => {
      const startTime = performance.now();
      const [err] = await YarismaService.getSoruCevapIndex();
      if (err) {
        LogService.log(LogSayfa.ANASAYFA, LogIslem.HATA, {
          detay: "sorularPreload",
          err,
          delay: performance.now() - startTime
        });
        return;
      }
      LogService.log(LogSayfa.ANASAYFA, LogIslem.ANASAYFA_SORU_PRELOAD, {
        detay: "sorularPreload",
        delay: performance.now() - startTime
      });
    });
    sorularTimer.start();
    return sorularTimer;
  }

  /**
   * Sınavın başlamasına ve bitmesine kalan süreleri kadar geri sayım başlatır.
   * @param baslamasinaKalanSure - Sınavın başlamasına kalan süre
   * @param bitmesineKalanSure - Sınavın bitmesine kalan süre
   * @returns TimerService
   */
  static baslamasinaKalanSureGeriSayimiBaslat(baslamasinaKalanSure: Ref<number>, sorularPreloadTimer: Ref<TimerService | undefined>): TimerService {
    const baslamasinaKalanSureTimer = new TimerService(
      baslamasinaKalanSure,
      async () => {
        YarismaService.setBaslamasinaKalanSure(Math.floor(baslamasinaKalanSure.value) - 1);
        if (baslamasinaKalanSure.value < 60 * 15 && !YonergeService.onaylandiMi()) {
          await router.push('yonerge')
          return;
        }
        // 2 dakikadan fazla veya 5 saniyeden az varsa çalışmasın.
        if (baslamasinaKalanSure.value > 60 * 2 || baslamasinaKalanSure.value - 5 < 0) return;

        // sorular geldiyse veya timer kurulduysa çalışmasın
        if (YarismaService.sorularGeldiMi() || sorularPreloadTimer.value) {
          return;
        }

        sorularPreloadTimer.value = YarismaService.sorularPreload(baslamasinaKalanSure.value);
      },
      async () => {
        await router.push("yarisma");
      }
    );
    baslamasinaKalanSureTimer.start();
    return baslamasinaKalanSureTimer;
  }

  static bitmesineKalanSureGeriSayimiBaslat(bitmesineKalanSure: Ref<number>): TimerService {
    const bitmesineKalanSureTimer = new TimerService(
      bitmesineKalanSure,
      async () => {
        YarismaService.setBitmesineKalanSure(Math.floor(bitmesineKalanSure.value) - 1);
      },
      async () => {
        StorageService.set(yarismaBittiMiKey(), true);
        LogService.log(LogSayfa.YARISMA, LogIslem.YARISMA_SURE_BITTI)
        await WarningPopup(i18n.global.t('popup.competition_time_ended'), i18n.global.t('popup.ok'))
        return await router.push('/?quite=true')
      }
    );
    bitmesineKalanSureTimer.start();
    return bitmesineKalanSureTimer;
  }

  static sorularGeldiMi(): boolean {
    return StorageService.get<ISoru[]>(soruKey()) !== null;
  }

  static setIndex(index: number, soruId: number): void {
    StorageService.set(indexKey(), index);
    LogService.log(LogSayfa.YARISMA, LogIslem.YARISMA_SORU_GORUNTULEME, {
      si: soruId,
      ci: index,
    })
  }

  static getYarismaBittiMi(): boolean {
    if (StorageService.get<boolean>(yarismaBittiMiKey()) === true) return true;
    return YarismaService.getyarismaciYarismayiBitirmisMi();

  }

  static setBaslamasinaKalanSure(sure: number): void {
    StorageService.set(baslamasinaKalanSureKey(), sure);
  }

  static getBaslamasinaKalanSure(): number | null {
    return StorageService.get<number>(baslamasinaKalanSureKey());
  }

  static setBitmesineKalanSure(sure: number): void {
    StorageService.set(bitmesineKalanSureKey(), sure);
  }

  static getBitmesineKalanSure(): number | null {
    return StorageService.get<number>(bitmesineKalanSureKey());
  }


  static getKalanSureLastFetchTime(): number | null {
    return StorageService.get<number>(kalanSureLastFetchTimeKey());
  }

  static setKalanSureLastFetchTime(time: number): void {
    StorageService.set(kalanSureLastFetchTimeKey(), time);
  }

  static clear(): void {
    StorageService.delete(soruKey());
    StorageService.delete(cevapKey());
    StorageService.delete(indexKey());
    StorageService.delete(baslamasinaKalanSureKey());
    StorageService.delete(bitmesineKalanSureKey());
    StorageService.delete(kalanSureLastFetchTimeKey());
    //StorageService.delete(yarismaBittiMiKey());
  }

  static basBitKalanSurelerClear(): void {
    StorageService.delete(baslamasinaKalanSureKey());
    StorageService.delete(bitmesineKalanSureKey());
  }

  static getyarismaciYarismayiBitirmisMi(): boolean {
    const val = StorageService.get<boolean>(yarismaciBitirdiMiKey());
    return !!val;
  }

  static setyarismaciYarismayiBitirmisMi(bool: boolean): void {
    StorageService.set(yarismaciBitirdiMiKey(), bool);
    if (bool) {
      StorageService.set(yarismaBittiMiKey(), true);
    }
  }

  static getyarismaciBaslattiMi(): boolean {
    const val = StorageService.get<boolean>(yarismaciBaslattiMiKey());
    return val ? true : false;
  }

  static setyarismaciBaslattiMi(bool: boolean): void {
    StorageService.set(yarismaciBaslattiMiKey(), bool);
  }
}

export default YarismaService;

function soruKey(): string {
  return "s:" + suffix();
}

function cevapKey(): string {
  return "yc:" + suffix();
}

function indexKey(): string {
  return "i:" + suffix();
}

function yarismaBittiMiKey(): string {
  return "yb:" + suffix();
}

function yarismaciBitirdiMiKey(): string {
  return "yab:" + suffix();
}

function baslamasinaKalanSureKey(): string {
  return "bsk:" + suffix();
}

function bitmesineKalanSureKey(): string {
  return "btk:" + suffix();
}

function kalanSureLastFetchTimeKey(): string {
  return "kslft:" + suffix();
}

function yarismaciBaslattiMiKey(): string {
  return "ybs:" + suffix();
}

function suffix(): string {
  const tokenObj = JwtService.getTokenObj();
  if (!tokenObj.yaid || !tokenObj.yid) {
    LogService.log(LogSayfa.YARISMA, LogIslem.HATA, {
      detay: "Invalid token: missing yaid or yid",
      tokenObj,
    });
    JwtService.destroyTokens()
    void router.push('/giris');
    return '0-0'; // fallback - login'e yönlendirildikten sonra önemli değil
  }
  return `${tokenObj.yaid}-${tokenObj.yid}`;
}
